import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../../services/adminApi';
import TextInput from '../../Common/TextInput';
import LoadingSpinner from '../../Common/LoadingSpinner';
import ConfirmationModal from '../../Common/ConfirmationModal';
import toast from 'react-hot-toast';

const getAssignmentLocationId = (assignment) => {
  const location = assignment?.locationId;
  if (!location) return '';
  if (typeof location === 'string') return location;
  return String(location._id || location.id || '');
};

const isInactiveAssignment = (assignment) =>
  assignment?.status && assignment.status !== 'active';

const UserLocationManagement = ({ user, onClose, onSuccess }) => {
  const [locations, setLocations] = useState([]);
  const [userLocations, setUserLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [assigningLocation, setAssigningLocation] = useState(false);
  const [activeTab, setActiveTab] = useState('assigned'); // 'assigned' or 'available'
  const [operatingLocationId, setOperatingLocationId] = useState(null);
  const [expandedLocationId, setExpandedLocationId] = useState(null);
  
  // Confirmation Modal State
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [locationToRevoke, setLocationToRevoke] = useState(null);
  const [revokeComment, setRevokeComment] = useState('');
  const [isRevoking, setIsRevoking] = useState(false);
  
  // Reassignment State
  const [availableUsers, setAvailableUsers] = useState([]);

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Reactivation Modal State
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [locationToReactivate, setLocationToReactivate] = useState(null);
  const [isReactivating, setIsReactivating] = useState(false);

  // Broken record delete state
  const [deletingAssignmentId, setDeletingAssignmentId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // arm-to-confirm

  // Fetch all locations and user locations on mount
  useEffect(() => {
    if (user) {
      fetchAllLocations();
      fetchUserLocations();
    }
  }, [user]);

  const fetchAllLocations = async () => {
    try {
      const response = await adminAPI.getActiveLocations();
      const locationsData = response.data?.data || response.data || [];
      setLocations(locationsData);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Failed to load locations');
    }
  };

  const fetchUserLocations = async () => {
    setLoadingLocations(true);
    try {
      const userId = user._id || user.id;
      const response = await adminAPI.getUserLocations(userId, { activeOnly: false });
      const userLocsData = response.data?.data || response.data || [];
      setUserLocations(userLocsData);
    } catch (error) {
      console.error('Failed to fetch user locations:', error);
      toast.error('Failed to load user locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  const buildAssignmentPayload = (locationId, options = {}) => {
    const userId = user._id || user.id;
    const previousAssignment = userLocations.find(
      (assignment) =>
        getAssignmentLocationId(assignment) === String(locationId) &&
        isInactiveAssignment(assignment)
    );

    return {
      userId,
      locationId,
      accessLevel: options.accessLevel || previousAssignment?.accessLevel || 'read',
      locationRole: options.locationRole || previousAssignment?.locationRole || 'location_user',
      ...options,
      ...(previousAssignment
        ? {
            forceUpdate: true,
            notes: options.notes || 'Access reactivated by admin',
          }
        : {}),
    };
  };

  const handleAssignLocation = async (locationId, options = {}) => {
    setAssigningLocation(true);
    try {
      const assignmentData = buildAssignmentPayload(locationId, options);
      const isReactivation = Boolean(assignmentData.forceUpdate);

      await adminAPI.assignLocationToUser(assignmentData);

      toast.success(
        isReactivation
          ? 'Location access reactivated successfully'
          : 'Location assigned successfully'
      );
      await fetchUserLocations();
      setSelectedLocations([]);
      
      // Stay on available tab if there are more locations to assign
      if (availableLocations.length <= 1) {
        setActiveTab('assigned');
      }
    } catch (error) {
      console.error('Failed to assign location:', error);
      if (error.response?.status === 409) {
        toast.error(error.response?.data?.message || 'User already has access to this location');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to assign locations');
      } else if (error.response?.status === 404) {
        toast.error('Location or user not found');
      } else {
        toast.error(error.response?.data?.message || 'Failed to assign location');
      }
    } finally {
      setAssigningLocation(false);
    }
  };

  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await adminAPI.getUsers({ 
        status: 'active',
        limit: 100,
        excludeId: user._id || user.id
      });
      const usersData = response.data?.data || response.data || [];
      setAvailableUsers(usersData.filter(u => (u._id || u.id) !== (user._id || user.id)));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users for reassignment');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRevokeLocation = async (locationId, locationName) => {
    setLocationToRevoke({ id: locationId, name: locationName });
    setRevokeComment('');
    setUserSearchTerm('');
    setShowRevokeModal(true);
  };

  const confirmRevokeLocation = async () => {
    if (!locationToRevoke) return;
    
    setIsRevoking(true);
    setOperatingLocationId(locationToRevoke.id);
    
    try {
      const userId = user._id || user.id;
      const reason = revokeComment || 'Access revoked by admin';
      
      // Revoke access from the user
      await adminAPI.removeUserLocationByIds(userId, locationToRevoke.id, reason);
      
      toast.success('Location access revoked successfully');
      
      // Refresh the entire page to ensure all data is up to date
      window.location.reload();
    } catch (error) {
      console.error('Failed to revoke location:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to revoke location access');
      } else if (error.response?.status === 404) {
        toast.error('Location assignment not found');
      } else {
        toast.error(error.response?.data?.message || 'Failed to revoke location access');
      }
    } finally {
      setIsRevoking(false);
      setOperatingLocationId(null);
    }
  };

  const resetRevokeModal = () => {
    setLocationToRevoke(null);
    setRevokeComment('');
    setUserSearchTerm('');
    setAvailableUsers([]);
  };

  const handleDeleteBrokenRecord = async (assignmentId) => {
    if (confirmDeleteId !== assignmentId) {
      // First click: arm the confirm
      setConfirmDeleteId(assignmentId);
      return;
    }
    // Second click: execute permanent delete
    try {
      setDeletingAssignmentId(assignmentId);
      setConfirmDeleteId(null);
      await adminAPI.removeUserLocation(assignmentId);
      toast.success('Broken assignment deleted');
      fetchUserLocations();
    } catch (error) {
      console.error('Failed to delete broken assignment:', error);
      toast.error(error.response?.data?.message || 'Failed to delete assignment');
    } finally {
      setDeletingAssignmentId(null);
    }
  };

  const cancelRevokeLocation = () => {
    setShowRevokeModal(false);
    resetRevokeModal();
  };

  const handleReactivateLocation = (locationId) => {
    // Find the expired location assignment to get its details first for confirmation
    const expiredLocation = userLocations.find(
      (assignment) =>
        getAssignmentLocationId(assignment) === String(locationId) &&
        isInactiveAssignment(assignment)
    );
    
    if (!expiredLocation) {
      toast.error('Location assignment not found');
      return;
    }
    
    // Show confirmation modal
    setLocationToReactivate({
      id: locationId,
      name: expiredLocation.locationId.name,
      assignment: expiredLocation
    });
    setShowReactivateModal(true);
  };

  const confirmReactivateLocation = async () => {
    if (!locationToReactivate) return;
    
    setIsReactivating(true);
    setOperatingLocationId(locationToReactivate.id);
    try {
      const userId = user._id || user.id;
      const expiredLocation = locationToReactivate.assignment;
      
      // Try to assign the location again - this should reactivate if backend supports it
      await adminAPI.assignLocationToUser({
        userId,
        locationId: locationToReactivate.id,
        accessLevel: expiredLocation.accessLevel || 'read',
        locationRole: expiredLocation.locationRole || 'location_user',
        isDefault: false, // Don't automatically restore default status
        isPrimary: false, // Don't automatically restore primary status
        assignedBy: 'system',
        notes: 'Access reactivated by admin',
        forceUpdate: true // Indicate this is a reactivation
      });
      
      toast.success('Location access reactivated successfully');
      await fetchUserLocations();
      setShowReactivateModal(false);
      setLocationToReactivate(null);
    } catch (error) {
      console.error('Failed to reactivate location:', error);
      const errorMessage = error.response?.data?.message || error.message;
      
      if (error.response?.status === 403) {
        toast.error('You do not have permission to reactivate location access');
      } else if (error.response?.status === 409) {
        toast.error('Cannot reactivate: User already has active access to this location', {
          duration: 4000
        });
      } else if (error.response?.status === 404) {
        toast.error('Location assignment not found - it may have been permanently removed');
      } else if (error.response?.status === 400) {
        toast.error(`Invalid request: ${errorMessage}`);
      } else {
        toast.error(`Failed to reactivate location access: ${errorMessage || 'Unknown error'}`);
      }
    } finally {
      setIsReactivating(false);
      setOperatingLocationId(null);
    }
  };

  const cancelReactivateLocation = () => {
    setShowReactivateModal(false);
    setLocationToReactivate(null);
  };

  const handleSetDefaultLocation = async (locationId) => {
    setOperatingLocationId(locationId);
    try {
      const userId = user._id || user.id;
      await adminAPI.setDefaultLocation(userId, locationId);
      
      toast.success('Default location updated successfully');
      await fetchUserLocations();
    } catch (error) {
      console.error('Failed to set default location:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to set default locations');
      } else if (error.response?.status === 404) {
        toast.error('Location or user not found');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update default location');
      }
    } finally {
      setOperatingLocationId(null);
    }
  };

  const handleSetPrimaryLocation = async (locationId) => {
    setOperatingLocationId(locationId);
    try {
      const userId = user._id || user.id;
      await adminAPI.setPrimaryLocation(userId, locationId);
      
      toast.success('Primary location updated successfully');
      await fetchUserLocations();
    } catch (error) {
      console.error('Failed to set primary location:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to set primary locations');
      } else if (error.response?.status === 404) {
        toast.error('Location or user not found');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update primary location');
      }
    } finally {
      setOperatingLocationId(null);
    }
  };

  // Filter locations for assignment
  const availableLocations = useMemo(() => {
    // Only exclude active assignments - allow expired/inactive/suspended locations to be reassigned
    const activeLocationIds = new Set(
      userLocations
        .filter((assignment) => assignment.status === 'active')
        .map(getAssignmentLocationId)
        .filter(Boolean)
    );

    return locations.filter((loc) => {
      const locId = String(loc._id || loc.id);
      const matchesSearch = !locationSearch ||
        loc.name?.toLowerCase().includes(locationSearch.toLowerCase()) ||
        loc.code?.toLowerCase().includes(locationSearch.toLowerCase()) ||
        loc.type?.toLowerCase().includes(locationSearch.toLowerCase());

      return !activeLocationIds.has(locId) && matchesSearch;
    });
  }, [locations, userLocations, locationSearch]);

  const sortedUserLocations = useMemo(() => {
    const statusOrder = { active: 0, suspended: 1, inactive: 2, expired: 3 };
    return [...userLocations].sort((a, b) => {
      const statusDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.updatedAt || b.assignedAt || 0) - new Date(a.updatedAt || a.assignedAt || 0);
    });
  }, [userLocations]);

  const handleClose = () => {
    setLocationSearch('');
    setSelectedLocations([]);
    setExpandedLocationId(null);
    setOperatingLocationId(null);
    onClose && onClose();
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const handleSuccessAndClose = () => {
    onSuccess && onSuccess();
    handleClose();
  };

  const userName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.email || 'Unknown User';
  const assignmentsWithMissingLocation = userLocations.filter((ul) => !ul?.locationId);

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-2xl border border-gray-100 max-h-[95vh] overflow-hidden flex flex-col">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-lg px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Manage User Locations</h3>
                <p className="text-xs text-white/80">{userName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  fetchAllLocations();
                  fetchUserLocations();
                }}
                disabled={loadingLocations}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all disabled:opacity-50"
                title="Refresh data"
              >
                {loadingLocations ? (
                  <LoadingSpinner size="sm" color="text-white" inline center={false} />
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                )}
              </button>
              <button
                onClick={handleClose}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 bg-gray-50 px-4">
          <div className="flex gap-2 -mb-px">
            <button
              onClick={() => setActiveTab('assigned')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'assigned'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Assigned Locations</span>
                {userLocations.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {userLocations.length}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('available')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
                activeTab === 'available'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Assign New</span>
                {availableLocations.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {availableLocations.length}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-5 gap-3 text-center">
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="text-lg font-semibold text-gray-900">{userLocations.length}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="text-lg font-semibold text-green-600">
                {userLocations.filter(ul => ul.status === 'active').length}
              </div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="text-lg font-semibold text-red-600">
                {userLocations.filter(ul => ul.status === 'expired' || ul.status === 'inactive' || ul.status === 'suspended').length}
              </div>
              <div className="text-xs text-gray-500">Expired</div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="text-lg font-semibold text-blue-600">
                {userLocations.filter(ul => ul.isDefault).length}
              </div>
              <div className="text-xs text-gray-500">Default</div>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <div className="text-lg font-semibold text-purple-600">
                {userLocations.filter(ul => ul.isPrimary).length}
              </div>
              <div className="text-xs text-gray-500">Primary</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingLocations ? (
            <div className="relative min-h-[300px]">
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
                <div className="text-center">
                  <LoadingSpinner size="lg" color="text-purple-600" inline showMessage={false} />
                  <p className="mt-3 text-gray-600 text-sm">Loading locations...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Assigned Locations Tab */}
              {activeTab === 'assigned' && (
                <div className="space-y-3">
                  {userLocations.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-sm text-gray-600 font-medium mb-1">No locations assigned</p>
                      <p className="text-xs text-gray-500 mb-3">This user doesn't have any location assignments yet</p>
                      <button
                        onClick={() => setActiveTab('available')}
                        className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-all"
                      >
                        <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Assign Locations
                      </button>
                    </div>
                  ) : (
                    <>
                      {assignmentsWithMissingLocation.length > 0 && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-red-800">
                              <span className="font-medium">Data issue detected:</span>{' '}
                              {assignmentsWithMissingLocation.length} assignment{assignmentsWithMissingLocation.length > 1 ? 's' : ''} point to missing location records.
                              Please verify location data or clean up orphan assignments.
                            </div>
                          </div>
                        </div>
                      )}

                      {userLocations.some(ul => ul.status === 'expired' || ul.status === 'inactive') && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="text-sm text-amber-800">
                              <span className="font-medium">Expired locations found:</span> You can reactivate expired locations by clicking the "Reactivate" button to restore user access with their previous permissions.
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 gap-3">
                        {sortedUserLocations.map((userLoc) => {
                        const location = userLoc.locationId;
                        if (!location) {
                          const isArmed = confirmDeleteId === userLoc._id;
                          const isDeleting = deletingAssignmentId === userLoc._id;
                          return (
                            <div
                              key={userLoc._id}
                              className="bg-white border border-red-200 rounded-lg p-4"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-semibold text-red-700">Missing location reference</h5>
                                  <p className="text-xs text-gray-600 mt-1 break-all">
                                    Assignment ID: {userLoc._id}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Status: {userLoc.status || 'unknown'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                    Broken record
                                  </span>
                                  <button
                                    onClick={() => handleDeleteBrokenRecord(userLoc._id)}
                                    disabled={isDeleting}
                                    title={isArmed ? 'Click again to confirm deletion' : 'Permanently delete this broken assignment'}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium border transition-colors ${
                                      isArmed
                                        ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                                        : 'bg-white text-red-600 border-red-300 hover:bg-red-50'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                  >
                                    {isDeleting ? (
                                      <>
                                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                        </svg>
                                        Deleting…
                                      </>
                                    ) : isArmed ? (
                                      <>
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Confirm Delete
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                      </>
                                    )}
                                  </button>
                                  {isArmed && !isDeleting && (
                                    <button
                                      onClick={() => setConfirmDeleteId(null)}
                                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                                    >
                                      Cancel
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        
                        return (
                          <div
                            key={userLoc._id}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-sm transition-all"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h5 className="font-semibold text-gray-900">{location.name}</h5>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    {location.code}
                                  </span>
                                  {userLoc.isDefault && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                      </svg>
                                      Default
                                    </span>
                                  )}
                                  {userLoc.isPrimary && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      Primary
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    userLoc.status === 'active' ? 'bg-green-100 text-green-700' :
                                    userLoc.status === 'inactive' ? 'bg-gray-100 text-gray-600' :
                                    userLoc.status === 'suspended' ? 'bg-orange-100 text-orange-700' :
                                    userLoc.status === 'expired' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {userLoc.status || 'unknown'}
                                  </span>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-3 text-xs text-gray-600 mb-2">
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    <span className="font-medium">Type:</span>
                                    <span className="ml-1">{location.type || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    <span className="font-medium">Access:</span>
                                    <span className="ml-1">{userLoc.accessLevel || 'read'}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span className="font-medium">Role:</span>
                                    <span className="ml-1">{userLoc.locationRole?.replace('location_', '') || 'user'}</span>
                                  </div>
                                  <div className="flex items-center">
                                    <svg className="w-3 h-3 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-medium">Assigned:</span>
                                    <span className="ml-1">{new Date(userLoc.assignedAt).toLocaleDateString()}</span>
                                  </div>
                                  {userLoc.effectiveTo && (userLoc.status === 'expired' || userLoc.status === 'inactive' || userLoc.status === 'suspended') && (
                                    <div className="flex items-center">
                                      <svg className="w-3 h-3 mr-1 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span className="font-medium text-red-600">Revoked:</span>
                                      <span className="ml-1 text-red-600">{new Date(userLoc.effectiveTo).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                </div>

                                {location.address && (
                                  <div className="flex items-center text-xs text-gray-500 mt-2">
                                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {location.address.city ? `${location.address.city}, ` : ''}
                                    {location.address.country || ''}
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col space-y-1 ml-4">
                                {!userLoc.isDefault && userLoc.status === 'active' && (
                                  <button
                                    onClick={() => handleSetDefaultLocation(location._id || location.id)}
                                    disabled={operatingLocationId === (location._id || location.id)}
                                    className="text-xs px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                    title="Set as default"
                                  >
                                    {operatingLocationId === (location._id || location.id) ? (
                                      <>
                                        <LoadingSpinner size="xs" color="text-blue-600" inline center={false} />
                                        <span>Setting...</span>
                                      </>
                                    ) : (
                                      <span>Set Default</span>
                                    )}
                                  </button>
                                )}
                                
                                {!userLoc.isPrimary && userLoc.status === 'active' && (
                                  <button
                                    onClick={() => handleSetPrimaryLocation(location._id || location.id)}
                                    disabled={operatingLocationId === (location._id || location.id)}
                                    className="text-xs px-3 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                    title="Set as primary"
                                  >
                                    {operatingLocationId === (location._id || location.id) ? (
                                      <>
                                        <LoadingSpinner size="xs" color="text-purple-600" inline center={false} />
                                        <span>Setting...</span>
                                      </>
                                    ) : (
                                      <span>Set Primary</span>
                                    )}
                                  </button>
                                )}
                                
                                {userLoc.status === 'active' && (
                                  <button
                                    onClick={() => handleRevokeLocation(location._id || location.id, location.name)}
                                    disabled={operatingLocationId === (location._id || location.id)}
                                    className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                    title="Revoke access"
                                  >
                                    {operatingLocationId === (location._id || location.id) ? (
                                      <>
                                        <LoadingSpinner size="xs" color="text-red-600" inline center={false} />
                                        <span>Revoking...</span>
                                      </>
                                    ) : (
                                      <span>Revoke</span>
                                    )}
                                  </button>
                                )}
                                
                                {(userLoc.status === 'expired' || userLoc.status === 'inactive' || userLoc.status === 'suspended') && (
                                  <button
                                    onClick={() => handleReactivateLocation(location._id || location.id)}
                                    disabled={operatingLocationId === (location._id || location.id)}
                                    className="text-xs px-3 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                                    title="Reactivate access"
                                  >
                                    {operatingLocationId === (location._id || location.id) ? (
                                      <>
                                        <LoadingSpinner size="xs" color="text-green-600" inline center={false} />
                                        <span>Reactivating...</span>
                                      </>
                                    ) : (
                                      <>
                                        <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        <span>Reactivate</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Available Locations Tab */}
              {activeTab === 'available' && (
                <div className="space-y-4">
                  {/* Search */}
                  <TextInput
                    variant="input"
                    type="text"
                    placeholder="Search available locations by name, code, or type..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    icon={({ className }) => (
                      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />

                  {/* Available Locations */}
                  {availableLocations.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="text-sm text-gray-600 font-medium">
                        {locationSearch ? 'No matching locations found' : 'All locations are already assigned'}
                      </p>
                      {locationSearch && (
                        <button
                          onClick={() => setLocationSearch('')}
                          className="mt-2 text-xs text-purple-600 hover:text-purple-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {availableLocations.map((location) => {
                        const locId = location._id || location.id;
                        const isSelected = selectedLocations.includes(locId);
                        
                        // Check if this location was previously assigned but is now expired
                        const previousAssignment = userLocations.find(
                          (assignment) =>
                            getAssignmentLocationId(assignment) === String(locId) &&
                            isInactiveAssignment(assignment)
                        );
                        
                        return (
                          <div
                            key={locId}
                            onClick={() => {
                              setSelectedLocations(prev => 
                                isSelected 
                                  ? prev.filter(id => id !== locId)
                                  : [...prev, locId]
                              );
                            }}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50 shadow-sm'
                                : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h5 className="font-semibold text-gray-900">{location.name}</h5>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                    {location.code}
                                  </span>
                                  {location.type && (
                                    <span className="text-xs text-gray-500">
                                      {location.type}
                                    </span>
                                  )}
                                  {previousAssignment && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                      Previously {previousAssignment.status}
                                    </span>
                                  )}
                                </div>
                                {location.address && (
                                  <div className="flex items-center text-xs text-gray-500">
                                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {location.address.city ? `${location.address.city}, ` : ''}
                                    {location.address.country || ''}
                                  </div>
                                )}
                              </div>
                              
                              <div className="ml-4 flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAssignLocation(locId, {
                                      accessLevel: 'read',
                                      locationRole: 'location_user',
                                    });
                                  }}
                                  disabled={assigningLocation}
                                  className={`text-xs px-2 py-1 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                    previousAssignment ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'
                                  }`}
                                  title={previousAssignment ? 'Reassign to reactivate access' : 'Quick assign with default settings'}
                                >
                                  {assigningLocation ? (previousAssignment ? 'Reactivating...' : 'Assigning...') : (previousAssignment ? 'Reactivate' : 'Assign')}
                                </button>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Assign Button */}
                  {selectedLocations.length > 0 && (
                    <div className="sticky bottom-0 mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg shadow-sm">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-purple-900">
                            {selectedLocations.length} location{selectedLocations.length > 1 ? 's' : ''} selected
                          </p>
                          <p className="text-xs text-purple-700">Will be assigned with default settings (read access, user role)</p>
                        </div>
                        <button
                          onClick={async () => {
                            if (selectedLocations.length === 1) {
                              await handleAssignLocation(selectedLocations[0], {
                                accessLevel: 'read',
                                locationRole: 'location_user',
                              });
                              return;
                            }

                            setAssigningLocation(true);
                            try {
                              const userId = user._id || user.id;
                              const assignmentPayloads = selectedLocations.map((locationId) =>
                                buildAssignmentPayload(locationId, {
                                  accessLevel: 'read',
                                  locationRole: 'location_user',
                                })
                              );

                              const response = await adminAPI.bulkAssignLocations(userId, assignmentPayloads);
                              const results = response.data?.data || {};
                              const successCount = results.successful?.length || 0;
                              const failedCount = results.failed?.length || 0;

                              if (successCount > 0) {
                                toast.success(`${successCount} location${successCount !== 1 ? 's' : ''} assigned successfully`);
                              }
                              if (failedCount > 0) {
                                const firstError = results.failed[0]?.error;
                                toast.error(
                                  failedCount === selectedLocations.length
                                    ? (firstError || 'Failed to assign selected locations')
                                    : `${failedCount} location${failedCount !== 1 ? 's' : ''} could not be assigned`
                                );
                              }

                              await fetchUserLocations();
                              setSelectedLocations([]);

                              if (availableLocations.length <= selectedLocations.length) {
                                setActiveTab('assigned');
                              }
                            } catch (error) {
                              console.error('Failed to assign locations:', error);
                              if (error.response?.status === 403) {
                                toast.error('You do not have permission to assign locations');
                              } else {
                                toast.error(error.response?.data?.message || 'Failed to assign locations');
                              }
                            } finally {
                              setAssigningLocation(false);
                            }
                          }}
                          disabled={assigningLocation}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-sm"
                        >
                          {assigningLocation ? (
                            <>
                              <LoadingSpinner size="sm" color="text-white" inline center={false} />
                              <span>Assigning...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              <span>Assign Selected</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-4 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500 flex items-center space-x-4">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{userLocations.length}</span>
                <span className="ml-1">assigned</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="font-medium">{availableLocations.length}</span>
                <span className="ml-1">available</span>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Revoke Confirmation Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-lg bg-white rounded-lg shadow-2xl border border-gray-100 mx-auto animate-in fade-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Revoke Location Access</h3>
              <button
                type="button"
                onClick={cancelRevokeLocation}
                disabled={isRevoking}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 mb-4">
                    Are you sure you want to revoke {user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}'s` : 'this user\'s'} access to "{locationToRevoke?.name}"? This action will immediately remove their access to this location.
                  </div>



                  {/* Comment box */}
                  <div>
                    <textarea
                      value={revokeComment}
                      onChange={(e) => setRevokeComment(e.target.value)}
                      placeholder="Optional: Add a reason for revoking access..."
                      rows={3}
                      disabled={isRevoking}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={cancelRevokeLocation}
                disabled={isRevoking}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRevokeLocation}
                disabled={isRevoking}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRevoking ? (
                  <div className="flex items-center space-x-2">
                    <LoadingSpinner size="sm" color="text-white" inline center={false} />
                    <span>Revoking...</span>
                  </div>
                ) : (
                  'Revoke Access'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReactivateModal}
        onClose={cancelReactivateLocation}
        onConfirm={confirmReactivateLocation}
        onCancel={cancelReactivateLocation}
        title="Reactivate Location Access"
        message={`Are you sure you want to reactivate ${user?.firstName && user?.lastName ? user.firstName + ' ' + user.lastName + "'s" : "this user's"} access to "${locationToReactivate?.name}"? This will restore their previous access level and permissions.`}
        confirmText="Reactivate Access"
        cancelText="Cancel"
        type="success"
        loading={isReactivating}
      />
    </div>
  );
};

export default UserLocationManagement;